import { __classPrivateFieldGet, __classPrivateFieldSet, __esDecorate, __runInitializers, __setFunctionName } from "tslib";
import { Viewer } from "./viewer";
import { CreateViewerForCanvas } from "./viewerFactory";
import { ViewerElementBase, parseColor } from "../viewerElementBase";
import { customElement, property } from "lit/decorators.js";
import { Color4 } from "core/Maths/math.color";
import { Deferred } from "core/Misc/deferred";
function coerceEngineAttribute(value) {
    if (value === "WebGL" || value === "WebGPU") {
        return value;
    }
    return undefined;
}
// Converts any standard html color string to a Color4 object.
function parseColorAsColor4(color) {
    const parsed = parseColor(color);
    return parsed ? new Color4(parsed.r, parsed.g, parsed.b, parsed.a) : null;
}
/**
 * Viewer custom element backed by the full Babylon.js engine.
 * Extends ViewerElementBase with Color4-typed clearColor, viewerDetails, and engine selection.
 */
let ViewerElement = (() => {
    var _a, _ViewerElement_clearColor_accessor_storage, _ViewerElement_engine_accessor_storage;
    let _classSuper = ViewerElementBase;
    let _clearColor_decorators;
    let _clearColor_initializers = [];
    let _clearColor_extraInitializers = [];
    let _engine_decorators;
    let _engine_initializers = [];
    let _engine_extraInitializers = [];
    return _a = class ViewerElement extends _classSuper {
            /**
             * Creates an instance of a ViewerElement subclass.
             * @param _viewerClass The Viewer subclass to use when creating the Viewer instance.
             * @param options The options to use when creating the Viewer and binding it to the specified canvas.
             */
            constructor(_viewerClass, options = {}) {
                super(options);
                this._viewerClass = _viewerClass;
                _ViewerElement_clearColor_accessor_storage.set(this, __runInitializers(this, _clearColor_initializers, this._options.clearColor
                    ? new Color4(this._options.clearColor[0], this._options.clearColor[1], this._options.clearColor[2], this._options.clearColor[3] ?? 1)
                    : null));
                _ViewerElement_engine_accessor_storage.set(this, (__runInitializers(this, _clearColor_extraInitializers), __runInitializers(this, _engine_initializers, this._options.engine)));
                __runInitializers(this, _engine_extraInitializers);
                this._viewerClass = _viewerClass;
            }
            /**
             * Gets the underlying viewer details (when the underlying viewer is in a loaded state).
             * This is useful for advanced scenarios where direct access to the viewer or Babylon scene is needed.
             */
            get viewerDetails() {
                return this._viewerDetails;
            }
            /**
             * The clear color (e.g. background color) for the viewer.
             */
            get clearColor() { return __classPrivateFieldGet(this, _ViewerElement_clearColor_accessor_storage, "f"); }
            set clearColor(value) { __classPrivateFieldSet(this, _ViewerElement_clearColor_accessor_storage, value, "f"); }
            /**
             * The engine to use for rendering.
             */
            get engine() { return __classPrivateFieldGet(this, _ViewerElement_engine_accessor_storage, "f"); }
            set engine(value) { __classPrivateFieldSet(this, _ViewerElement_engine_accessor_storage, value, "f"); }
            _needsReload(changedProperties) {
                if (super._needsReload(changedProperties)) {
                    return true;
                }
                if (changedProperties.has("engine")) {
                    const previous = changedProperties.get("engine");
                    if (previous && this.engine !== previous) {
                        return true;
                    }
                }
                return false;
            }
            async _createViewer(canvas, options) {
                const detailsDeferred = new Deferred();
                // Wrap the base class's proxied options to add engine and onInitialized interception.
                const fullOptions = new Proxy(options, {
                    get: (target, prop) => {
                        switch (prop) {
                            case "engine":
                                return this.engine ?? target.engine;
                            case "onInitialized":
                                return (details) => {
                                    target.onInitialized?.(details);
                                    detailsDeferred.resolve(details);
                                };
                            default:
                                return target[prop];
                        }
                    },
                });
                const viewer = (await CreateViewerForCanvas(canvas, Object.assign(fullOptions, { viewerClass: this._viewerClass })));
                const details = await detailsDeferred.promise;
                this._viewerDetails = Object.assign(details, { viewer });
                return viewer;
            }
            _onViewerTornDown() {
                this._viewerDetails = undefined;
            }
        },
        _ViewerElement_clearColor_accessor_storage = new WeakMap(),
        _ViewerElement_engine_accessor_storage = new WeakMap(),
        (() => {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
            _clearColor_decorators = [property({
                    attribute: "clear-color",
                    converter: {
                        fromAttribute: parseColorAsColor4,
                        toAttribute: (color) => (color ? color.toHexString() : null),
                    },
                })];
            _engine_decorators = [property({ converter: coerceEngineAttribute })];
            __esDecorate(_a, null, _clearColor_decorators, { kind: "accessor", name: "clearColor", static: false, private: false, access: { has: obj => "clearColor" in obj, get: obj => obj.clearColor, set: (obj, value) => { obj.clearColor = value; } }, metadata: _metadata }, _clearColor_initializers, _clearColor_extraInitializers);
            __esDecorate(_a, null, _engine_decorators, { kind: "accessor", name: "engine", static: false, private: false, access: { has: obj => "engine" in obj, get: obj => obj.engine, set: (obj, value) => { obj.engine = value; } }, metadata: _metadata }, _engine_initializers, _engine_extraInitializers);
            if (_metadata) Object.defineProperty(_a, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        })(),
        _a;
})();
export { ViewerElement };
/**
 * Displays a 3D model using the Babylon.js Viewer.
 */
let HTML3DElement = (() => {
    let _classDecorators = [customElement("babylon-viewer")];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = ViewerElement;
    var HTML3DElement = _classThis = class extends _classSuper {
        /**
         * Creates a new HTML3DElement.
         * @param options The options to use for the viewer. This is optional, and is only used when programmatically creating a viewer element.
         */
        constructor(options) {
            super(Viewer, options);
        }
    };
    __setFunctionName(_classThis, "HTML3DElement");
    (() => {
        const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
        __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
        HTML3DElement = _classThis = _classDescriptor.value;
        if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
        __runInitializers(_classThis, _classExtraInitializers);
    })();
    return HTML3DElement = _classThis;
})();
export { HTML3DElement };
/**
 * Creates a custom HTML element that creates an HTML3DElement with the specified name and configuration.
 * @param elementName The name of the custom element.
 * @param options The options to use for the viewer.
 */
export function ConfigureCustomViewerElement(elementName, options) {
    customElements.define(elementName, 
    // eslint-disable-next-line jsdoc/require-jsdoc
    class extends HTML3DElement {
        constructor() {
            super(options);
        }
    });
}
//# sourceMappingURL=viewerElement.js.map